import { useCallback, useMemo } from 'react';
import { NavItem } from '@im/ui-lib/nav';
import { useTranslate } from '@im/react-localization';
import {
  useMyBusinessMenu,
  MyBusinessMenuItem,
} from '../../hooks/use-mybusinessmenu';
import { useVoucherCountAPI } from '../../hooks/use-vouchercount';

export function useMyBusinessNavItems() {
  const translate = useTranslate();
  const { data: vouchers, isLoading: isVoucherLoading } = useVoucherCountAPI();
  const { data: backofficeMenuItems } = useMyBusinessMenu();

  // You may write custom logic to render a dynamic url by
  // setting the value to a function that returns the desired url or route.
  const srcUrlMap: { [itemName: string]: string } = useMemo(() => {
    return {
      EXAMPLE_SOURCE_URL: 'https://www.ingrammicro.com',
    };
  }, []);

  const menuItems = useMemo(() => {
    if (
      backofficeMenuItems &&
      !isVoucherLoading &&
      !(vouchers && vouchers.count > 0)
    ) {
      const voucherMenuIndex = backofficeMenuItems.findIndex(
        (m: MyBusinessMenuItem) => m.name === 'Vouchers'
      );
      if (voucherMenuIndex > -1)
        backofficeMenuItems.splice(voucherMenuIndex, 1);
    }
    backofficeMenuItems
      ?.filter((m: MyBusinessMenuItem) => m.url === 'FROM_SOURCE')
      .forEach((m: MyBusinessMenuItem) => {
        m.url = srcUrlMap[m.name];
        if (!m.url) {
          console.warn(
            `No SOURCE URL supplied for dynamic My Business menu item "${m.name}". Update source code or provide URL in Business Menu BO configuration.`
          );
        }
      });
    return backofficeMenuItems;
  }, [backofficeMenuItems, isVoucherLoading, srcUrlMap, vouchers]);

  // If language is changed, translate method returns empty string first and then actual value
  // because it loads the translation through xhr call which consumes few ms.
  // This constant is used to trigger 'buildMyBusinessMenu' callback fn when the translation is loaded.
  const menuTranslateCheck = translate('Navigation.MyBusiness');

  const buildMyBusinessMenu = useCallback(
    (items: MyBusinessMenuItem[] | undefined) => {
      if (!items) return [];
      return items
        .filter((item) => item.url !== undefined)
        .map((item) => {
          const { name, openInNewTab, subMenu, translationKey } = item;
          const navItem: NavItem = {
            key: name,
            label: translate(translationKey),
          };

          if (name === 'Vouchers' && vouchers && vouchers.count > 0) {
            navItem.label = `${translate(translationKey)}(${vouchers.count})`;
          }

          const isRelPath = !item?.url?.startsWith('http');

          if (!openInNewTab) {
            if (isRelPath) navItem.route = item.url;
            //urls open in new tab by default, override with onClick
            else navItem.onClick = () => (window.location.href = item.url);
          } else if (isRelPath)
            //rel paths in new tabs need base path
            navItem.onClick = () =>
              window.open(`/cep/app${item.url}`, '_blank');
          else navItem.url = item.url;

          if (subMenu) navItem.navItems = buildMyBusinessMenu(subMenu);

          return navItem;
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menuTranslateCheck, vouchers]
  );
  return useMemo(() => {
    return buildMyBusinessMenu(menuItems);
  }, [menuItems, buildMyBusinessMenu]);
}
